// src/api.js
// Single place for talking to your FastAPI backend at /api/...

const API_BASE = "/api";

// ---------------------------------------------------------------------
// Tenant helpers
// ---------------------------------------------------------------------
function getTenantFromHost() {
  const host = window.location.hostname;

  // -------------------------------
  // LOCAL DEVELOPMENT
  // -------------------------------
  if (host === "localhost" || host === "127.0.0.1") {
    return "dfasio"; // current local dev tenant that exists in Postgres
  }

  // -------------------------------
  // PRODUCTION: public marketing site
  // -------------------------------
  if (host === "jobtelesis.com" || host === "www.jobtelesis.com") {
    return "root"; // public / marketing tenant context
  }

  // -------------------------------
  // FUTURE: tenant subdomains
  // ex: dfasio.jobtelesis.com → "dfasio"
  // -------------------------------
  if (host.endsWith(".jobtelesis.com")) {
    return host.split(".")[0];
  }

  // -------------------------------
  // FALLBACK
  // -------------------------------
  return "jobtelesis";
}

export function getTenant() {
  const override = (localStorage.getItem("x-tenant") || "").trim();

  // If user selected a workspace (login), ALWAYS use it
  if (override) return override;

  // Otherwise fallback (for marketing site or no login yet)
  return getTenantFromHost();
}

// ---------------------------------------------------------------------
// Lightweight fetch helper (backwards compatibility)
// ---------------------------------------------------------------------
async function apiFetch(path, options = {}) {
  const p = (path || "").toString();

  const url =
    p.startsWith("http://") ||
    p.startsWith("https://") ||
    p.startsWith("/")
      ? p
      : `/${p}`;

  return fetch(url, {
    credentials: "include",
    headers: {
      "x-tenant": getTenant(),
      ...(options.headers || {}),
    },
    ...options,
  });
}

/**
 * Core fetch helper that:
 *  - calls your FastAPI through Nginx at /api/...
 *  - always sends cookies (credentials: "include")
 *  - always sends x-tenant
 *  - returns JSON when content-type is JSON, else text
 *  - throws helpful errors for non-OK responses
 */
export async function apiRequest(
  path,
  { method = "GET", headers = {}, body, ...rest } = {}
) {
  const init = {
    method,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...headers,
    },
    ...rest,
  };

  if (body !== undefined) {
    init.body = typeof body === "string" ? body : JSON.stringify(body);
  }

  // Normalize so callers can pass "/clients/.." OR "/api/clients/.."
  const normalizedPath = path.startsWith("/api/") ? path : `${API_BASE}${path}`;

  init.headers = {
    ...init.headers,
    "x-tenant": getTenant(),
  };

  const res = await fetch(normalizedPath, init);

  if (res.status === 401) {
    const publicPaths = [
      "/login",
      "/signup",
      "/beta-signup",
      "/app/beta-signup",
      "/workspace-created",
      "/app/workspace-created",
      "/forgot-password",
      "/reset-password",
    ];

    const currentPath = window.location.pathname;
    const isPublicPage = publicPaths.includes(currentPath);

    if (!isPublicPage) {
      localStorage.setItem(
        "session_message",
        "Your session expired. Please log in again."
      );
      window.location.replace("/login");
    }

    const error = new Error("Session expired");
    error.status = 401;
    throw error;
  }

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    const error = new Error(
      `${res.status} ${res.statusText}${text ? `: ${text}` : ""}`
    );

    error.status = res.status;
    error.body = text;

    throw error;
  }
  
const ct = res.headers.get("content-type") || "";
  if (ct.includes("application/json")) {
    return res.json();
  }

  return res.text();
}

/**
 * Backwards-compat alias
 */
export const apiCall = (path, options = {}) => apiRequest(path, options);

/* ----------------------------- Auth ----------------------------- */

export const AuthAPI = {
  // Email-only login. Backend finds the tenant from the user's email.
  login(username, password) {
    return fetch(`${API_BASE}/auth/login`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email: username, password }),
    }).then(async (r) => {
    if (!r.ok) {
      const t = await r.text().catch(() => "");
      throw new Error(`${r.status} ${r.statusText}${t ? `: ${t}` : ""}`);
    }

    // Save tenant returned by backend after email-only login
    const data = await r.json();

    if (data?.tenant_slug) {
      localStorage.setItem("x-tenant", data.tenant_slug);
    }

    return data;
  });
},

  logout() {
    return fetch(`${API_BASE}/auth/logout`, {
      method: "POST",
      credentials: "include",
      headers: {
        "x-tenant": getTenant(),
      },
    });
  },

  me() {
    return apiRequest(`/auth/me`);
  },

  forgotPassword(email) {
    return apiRequest(`/auth/forgot-password`, {
      method: "POST",
      body: { email },
    });
  },

  resetPassword(token, newPassword) {
    return apiRequest(`/auth/reset-password`, {
      method: "POST",
      body: {
        token,
        new_password: newPassword,
      },
    });
  },

  changePassword(currentPassword, newPassword) {
    return apiRequest(`/auth/change-password`, {
      method: "POST",
      body: {
        current_password: currentPassword,
        new_password: newPassword,
      },
    });
  },
};

/* ----------------------------- Jobs ----------------------------- */

export const JobsAPI = {
  patchStatus(jobId, status) {
    return apiRequest(`/jobs/${jobId}/status`, {
      method: "PATCH",
      body: { status },
    });
  },
};

/* --------------------------- Servers ---------------------------- */

export const ServersAPI = {
  list: (q = "") =>
    apiRequest(`/servers${q ? `?q=${encodeURIComponent(q)}` : ""}`),
  create: (payload) =>
    apiRequest(`/servers`, { method: "POST", body: payload }),
  get: (id) => apiRequest(`/servers/${id}`),
  notes: (id) => apiRequest(`/servers/${id}/notes`),
  addNote: (id, note) =>
    apiRequest(`/servers/${id}/notes`, { method: "POST", body: note }),
};

/* ---------------------------- Clients --------------------------- */

export function fetchClient(id) {
  return apiRequest(`/clients/${id}`);
}

export function updateClient(id, payload) {
  return apiRequest(`/clients/${id}`, { method: "PUT", body: payload });
}

export function fetchClientJobs(id) {
  return apiRequest(`/clients/${id}/jobs`);
}

export function fetchClientInvoices(id) {
  return apiRequest(`/clients/${id}/invoices`);
}

export function fetchClientReceipts(id) {
  return apiRequest(`/clients/${id}/receipts`);
}

export function fetchClientNotes(id) {
  return apiRequest(`/client-notes/${id}`);
}

export function createClientNote(id, content) {
  return apiRequest(`/client-notes/${id}`, {
    method: "POST",
    body: { content },
  });
}

export const ClientsAPI = {
  get: fetchClient,
  update: updateClient,
  jobs: fetchClientJobs,
  invoices: fetchClientInvoices,
  receipts: fetchClientReceipts,
  notes: fetchClientNotes,
  addNote: createClientNote,
};

/* ------------------------ Client Summary ------------------------ */

export async function fetchClientSummary(clientId) {
  return apiRequest(`/clients/${clientId}/summary`);
}

/* -------------------------- Thumbtack --------------------------- */

export const ThumbtackAPI = {
  list: () => apiRequest("/integrations/thumbtack/connections"),
  create: (payload) =>
    apiRequest("/integrations/thumbtack/connections", {
      method: "POST",
      body: payload,
    }),
  rotate: (connectionId) =>
    apiRequest(
      `/integrations/thumbtack/connections/${connectionId}/rotate-credentials`,
      { method: "POST" }
    ),
  disable: (connectionId) =>
    apiRequest(`/integrations/thumbtack/connections/${connectionId}`, {
      method: "DELETE",
    }),
  leads: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.set("status", filters.status);
    if (filters.unread !== undefined && filters.unread !== "") {
      params.set("unread", String(filters.unread));
    }
    if (filters.archived !== undefined && filters.archived !== "") {
      params.set("archived", String(filters.archived));
    }
    if (filters.q) params.set("q", filters.q);
    const query = params.toString();
    return apiRequest(
      `/integrations/thumbtack/leads${query ? `?${query}` : ""}`
    );
  },
  lead: (leadId) =>
    apiRequest(`/integrations/thumbtack/leads/${leadId}`),
  updateLead: (leadId, payload) =>
    apiRequest(`/integrations/thumbtack/leads/${leadId}`, {
      method: "PATCH",
      body: payload,
    }),
  addLeadActivity: (leadId, payload) =>
    apiRequest(`/integrations/thumbtack/leads/${leadId}/activities`, {
      method: "POST",
      body: payload,
    }),
  convertLead: (leadId) =>
    apiRequest(`/integrations/thumbtack/leads/${leadId}/convert`, {
      method: "POST",
    }),
};

/* ------------------------ Default export ------------------------ */

const api = {
  apiRequest,
  apiCall,
  AuthAPI,
  JobsAPI,
  ServersAPI,
  ClientsAPI,
  ThumbtackAPI,
  fetchClientSummary,
};

export default api;
